export type FeatureStudioNodeLevel = "PRODUCT" | "FEATURE" | "USERSTORY";

export interface FeatureStudioDeepLink {
    projectId: number;
    level: FeatureStudioNodeLevel;
    nodeId?: string;
}

const PRODUCT_PATH = /^\/products\/(\d+)\/?$/;
const NODE_PREFIXES: Record<string, FeatureStudioNodeLevel> = {
    P: "PRODUCT",
    F: "FEATURE",
    S: "USERSTORY"
};

export function parseFeatureStudioDeepLink(pathname: string, search = ""): FeatureStudioDeepLink | null {
    const match = PRODUCT_PATH.exec(pathname);
    if (!match) return null;

    const projectId = Number(match[1]);
    if (!Number.isSafeInteger(projectId) || projectId <= 0) return null;

    const params = new URLSearchParams(search);
    const view = params.get("view");
    if (view !== null && view !== "feature-studio") return null;

    const rawNode = params.get("node")?.trim();
    if (view === null && rawNode) return null;
    if (!rawNode) return { projectId, level: "PRODUCT" };

    const separator = rawNode.indexOf(":");
    if (separator <= 0) return null;
    const prefix = rawNode.slice(0, separator).toUpperCase();
    const nodeId = rawNode.slice(separator + 1).trim();
    const level = NODE_PREFIXES[prefix];
    if (!level || !nodeId) return null;

    return {
        projectId,
        level,
        ...(level === "PRODUCT" ? {} : { nodeId })
    };
}

export function featureStudioDeepLinkKey(link: FeatureStudioDeepLink): string {
    return `${link.projectId}:${link.level}:${link.nodeId ?? ""}`;
}

export function parseSameOriginFeatureStudioHref(
    href: string,
    origin: string
): { link: FeatureStudioDeepLink; path: string } | null {
    let url: URL;
    try {
        url = new URL(href, origin);
    } catch {
        return null;
    }
    if (url.origin !== origin) return null;
    const link = parseFeatureStudioDeepLink(url.pathname, url.search);
    if (!link) return null;
    return { link, path: `${url.pathname}${url.search}${url.hash}` };
}
